import { useState } from 'react';
import { Link } from 'react-router-dom';
import { buttonClass } from '@castinflow/ui';
import { apiFetch } from '../lib/api';
import { useLinkToken } from '../lib/link-token';
import { useSession } from '../lib/session';
import { usePageMeta } from '../shell/page-meta';
import { PublicCard } from '../shell/PublicLayout';
import { tr } from '../i18n/tr';
import { LinkNotice, LinkPageTitle, NewPasswordForm, linkErrorText } from './link-page-parts';

/**
 * Parola sıfırlama (M7 T2) — e-postadaki sıfırlama bağlantısıyla açılan oturumsuz sayfa. Kullanıcı yeni parolasını belirler
 * (`POST /auth/password/reset`); sunucu belirteci tüketir ve kullanıcının bütün oturumlarını kapatır. Oturum AÇILMAZ:
 * kullanıcı yeni parolasıyla giriş sayfasından girer.
 *
 * Belirteç kuralları davet kabulüyle aynıdır (`useLinkToken`): adres çubuğundan kaldırılır, yalnız istek gövdesinde gider.
 * Sunucu geçersiz, süresi dolmuş ve kullanılmış bağlantıyı ayırt etmez; sayfa üç olasılığı birlikte söyler.
 */
export function ResetPasswordPage() {
  usePageMeta(tr.auth.resetTitle);
  const token = useLinkToken();
  const reload = useSession((s) => s.load);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [phase, setPhase] = useState<'form' | 'rejected' | 'done'>('form');

  async function reset(password: string) {
    if (token === null) return;
    setBusy(true);
    setError(null);
    try {
      await apiFetch<unknown>('/auth/password/reset', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ token, password }),
      });
      // Sunucu kullanıcının oturumlarını kapattı: bu tarayıcıdaki oturum durumu yeniden okunur (giriş sayfası açılabilsin)
      await reload();
      setPhase('done');
    } catch (e) {
      const text = linkErrorText(e);
      if (text === null) setPhase('rejected');
      else setError(text);
    } finally {
      setBusy(false);
    }
  }

  const forgotLink = (
    <Link to="/forgot-password" className={buttonClass('secondary')} data-testid="reset-forgot">
      {tr.auth.forgotLink}
    </Link>
  );

  return (
    <PublicCard>
      <div className="space-y-4" data-testid="reset-page">
        <LinkPageTitle>{tr.auth.resetTitle}</LinkPageTitle>
        {phase === 'done' ? (
          <LinkNotice
            testId="reset-done"
            tone="success"
            title={tr.auth.resetDoneTitle}
            body={tr.auth.resetDoneBody}
          />
        ) : token === null ? (
          <LinkNotice
            testId="reset-missing"
            tone="warning"
            title={tr.auth.resetMissingTitle}
            body={tr.auth.resetMissingBody}
            action={forgotLink}
          />
        ) : phase === 'rejected' ? (
          <LinkNotice
            testId="reset-invalid"
            tone="danger"
            title={tr.auth.resetInvalidTitle}
            body={tr.auth.resetInvalidBody}
            action={forgotLink}
          />
        ) : (
          <NewPasswordForm
            testId="reset"
            submitLabel={tr.auth.resetSubmit}
            busy={busy}
            error={error}
            onSubmit={(password) => {
              void reset(password);
            }}
          >
            <p className="text-base text-fg-muted">{tr.auth.resetIntro}</p>
          </NewPasswordForm>
        )}
      </div>
    </PublicCard>
  );
}
